import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Avatar, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Divider, 
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  IconButton,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './ProfileEdit.css';
import { apiService } from '../utils/api';
import config from '../config';
import LinkIcon from '@mui/icons-material/Link';

const CUP_SIZES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];

function ProfileEdit() {
  const navigate = useNavigate();
  const { currentUser, loading } = useAuth();
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState({
    displayName: '',
    username: '',
    bio: '',
    location: '',
    website: '',
    avatar: '',
    coverImage: '',
    birthDate: '',
    gender: '',
    prefecture_code: '',
    phoneNumber: ''
  });
  // 電話番号変更用の状態
  const [newPhone, setNewPhone] = useState('');
  const [phoneChangeMsg, setPhoneChangeMsg] = useState('');
  const [phoneChangeErr, setPhoneChangeErr] = useState('');
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);
  
  // 投稿ユーザープロフィール用の状態
  const [isPosterUser, setIsPosterUser] = useState(false);
  const [posterProfile, setPosterProfile] = useState({
    shop_name: '',
    prefecture_code: '',
    city_code: '',
    stage_name: '',
    age: '',
    height: '',
    bust: '',
    cup_size: '',
    waist: '',
    hip: ''
  });
  
  // 地域データ用の状態
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  
  // アバター画像用のプレビューURL
  const [avatarPreview, setAvatarPreview] = useState('');
  // カバー画像用のプレビューURL
  const [coverPreview, setCoverPreview] = useState('');
  
  // 初期化時のデータ読み込み
  useEffect(() => {
    if (loading) return;
    
    // ユーザーがログインしていない場合はログインページへリダイレクト
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    // 都道府県データの取得
    fetchPrefectures();
    
    // ユーザーデータの取得 - auth/profileエンドポイントから現在のユーザー情報を取得
    const fetchUserProfile = async () => {
      try {
        // まず、認証済みユーザーの基本情報を取得
        const authProfileResponse = await apiService.get('/api/auth/profile');
        
        // 次に、ユーザー詳細情報を取得
        const userResponse = await apiService.get(`/api/users/${currentUser.username}`);
        const userData = userResponse.data;
        
        // 都道府県コードの取得試行 - 複数の可能なソースから
        let prefectureCode = null;
        
        // 1. ユーザーモデルから直接
        if (userData.prefecture_code) {
          prefectureCode = userData.prefecture_code;
        } 
        // 2. 都道府県オブジェクトから
        else if (userData.prefecture && userData.prefecture.code) {
          prefectureCode = userData.prefecture.code;
        }
        
        // 3. 認証プロフィールから
        else if (authProfileResponse.data.prefecture_code) {
          prefectureCode = authProfileResponse.data.prefecture_code;
        }
        
        // 4. データベースから直接取得するため、認証済みユーザー情報を使用
        if (!prefectureCode) {
          try {
            // ユーザーIDを使用してデータベースからユーザー情報を直接取得
            const dbUserResponse = await apiService.get(`/api/users/profile/${currentUser.id}`);
            const dbUserData = dbUserResponse.data;
            
            if (dbUserData.prefecture_code) {
              prefectureCode = dbUserData.prefecture_code;
            }
            
            // それでもなければ、roleフィールドから取得を試みる
            else if (dbUserData.role && dbUserData.role.prefecture_code) {
              prefectureCode = dbUserData.role.prefecture_code;
            }
          } catch (err) {
            
          }
        }
        
        // 相対パスを絶対パスに変換
        let avatarUrl = userData.avatar || '';
        let coverUrl = userData.cover_image || '';
        
        if (avatarUrl && avatarUrl.startsWith('/')) {
          avatarUrl = `${config.API_BASE_URL}${avatarUrl}`;
        }
        
        if (coverUrl && coverUrl.startsWith('/')) {
          coverUrl = `${config.API_BASE_URL}${coverUrl}`;
        }
        
        setProfileData({
          displayName: userData.display_name || userData.displayName || '',
          username: userData.username || '',
          bio: userData.bio || '',
          location: userData.location || '',
          website: userData.website || '',
          avatar: avatarUrl,
          coverImage: coverUrl,
          avatarUrl: avatarUrl,
          coverUrl: coverUrl,
          birthDate: userData.birth_date || '',
          gender: userData.gender || '',
          prefecture_code: prefectureCode || '',
          phoneNumber: userData.phone_number || ''
        });
        
        setAvatarPreview(avatarUrl || '');
        setCoverPreview(coverUrl || '');
        
        console.log('Profile images loaded:');
        console.log('Avatar:', userData.avatar);
        console.log('Cover image:', userData.cover_image);
        console.log('Avatar preview set to:', avatarUrl || '');
        console.log('Cover preview set to:', coverUrl || '');
        
        // 投稿ユーザー情報があれば設定
        if (userData.poster_profile) {
          setIsPosterUser(true);
          const profile = userData.poster_profile;
          
          setPosterProfile({
            shop_name: profile.shop_name || '',
            prefecture_code: profile.prefecture?.code || '',
            city_code: profile.city?.code || '',
            stage_name: profile.stage_name || '',
            age: profile.age || '',
            height: profile.height || '',
            bust: profile.bust || '',
            cup_size: profile.cup_size || '',
            waist: profile.waist || '',
            hip: profile.hip || ''
          });
          
          // 都道府県が選択されている場合は市区町村を読み込む
          if (profile.prefecture?.code) {
            fetchCities(profile.prefecture.code);
          }
        }
        
        // 都道府県コードが取得できた場合は市区町村を読み込む
        if (prefectureCode) {
          fetchCities(prefectureCode);
        }
        
        setPageLoading(false);
      } catch (err) {
        
        
        // バックエンドAPIが利用できない場合はモックデータを使用
        if (currentUser.username) {
          setProfileData({
            displayName: currentUser.displayName || '管理者',
            username: currentUser.username || 'admin_user',
            bio: currentUser.bio || 'SNSプロジェクトの管理者アカウントです。',
            location: currentUser.location || '東京',
            website: currentUser.website || 'https://example.com',
            avatar: currentUser.avatar || 'https://picsum.photos/id/1010/128/128',
            coverImage: currentUser.coverImage || 'https://picsum.photos/id/1015/1500/500',
            avatarUrl: currentUser.avatar || 'https://picsum.photos/id/1010/128/128',
            coverUrl: currentUser.coverImage || 'https://picsum.photos/id/1015/1500/500',
            birthDate: currentUser.birthDate || '',
            gender: currentUser.gender || '',
            prefecture_code: currentUser.prefecture_code || ''
          });
          setAvatarPreview(currentUser.avatar || '');
          setCoverPreview(currentUser.coverImage || '');
        }
        
        setPageLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [currentUser, loading, navigate]);

  // 都道府県データの取得
  const fetchPrefectures = async () => {
    try {
      const response = await apiService.get('/api/areas/prefectures');
      setPrefectures(response.data);
    } catch (error) {
      
    }
  };

  // 市区町村データの取得（都道府県が選択された場合）
  const fetchCities = async (prefectureCode) => {
    if (!prefectureCode) return;
    
    try {
      const response = await apiService.get(`/api/areas/cities/${prefectureCode}`);
      setCities(response.data);
    } catch (error) {
      
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };
  
  // 投稿ユーザープロフィール入力の変更を処理
  const handlePosterInputChange = (e) => {
    const { name, value } = e.target;
    
    // 都道府県が変更された場合の特別処理
    if (name === 'prefecture_code') {
      // 都道府県変更時に市区町村をクリアして、新しい市区町村リストを取得
      setPosterProfile(prev => ({
        ...prev,
        [name]: value,
        city_code: '' // 市区町村をリセット
      }));
      
      // 新しい市区町村リストを取得
      fetchCities(value);
    } else {
      // 通常の入力値変更処理
      setPosterProfile(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 数値フィールドの検証
  const validateNumberField = (value, min, max) => {
    if (!value) return true;
    const num = parseInt(value);
    return !isNaN(num) && num >= min && num <= max;
  };

  // 生年月日のバリデーション（18歳以上であることを確認）
  const validateBirthDate = (birthDate) => {
    if (!birthDate) return true;
    
    const today = new Date();
    const birthdateObj = new Date(birthDate);
    
    // 18年前の日付を計算
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 18);
    
    // 選択された生年月日が18年以上前であるかを確認
    return birthdateObj <= minDate;
  };

  // 画像アップロード関数
  const uploadImage = async (file, type) => {
    try {
      // TweetBoxと同じ方式でBase64に変換
      const reader = new FileReader();
      
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const base64Data = e.target.result;
            
            // ファイルサイズチェック
            const blob = await fetch(base64Data).then(r => r.blob());
            if (blob.size > 5 * 1024 * 1024) {
              throw new Error('画像ファイルサイズが5MBを超えています');
            }
            
            // ファイルタイプを取得
            let fileType = "image/jpeg";
            if (base64Data.startsWith('data:image/png')) {
              fileType = "image/png";
            } else if (base64Data.startsWith('data:image/gif')) {
              fileType = "image/gif";
            } else if (base64Data.startsWith('data:image/webp')) {
              fileType = "image/webp";
            }
            
            console.log('プロフィール画像アップロード:', {
              type: fileType,
              sizeInMB: (blob.size / (1024 * 1024)).toFixed(2),
              dataUrlLength: base64Data.length,
              uploadType: type
            });
            
                         // TweetBoxと同じ形式でAPIに送信（api.phpがBase64→ファイル変換を処理）
             const response = await apiService.post('/api/profiles/upload/image', {
               image_data: base64Data,
               image_type: fileType,
               type: type
             }, {
               headers: {
                 'Content-Type': 'application/json'
               }
             });
            
            if (response.data && response.data.image_url) {
              console.log('画像アップロード成功:', response.data.image_url);
              resolve(response.data.image_url);
            } else {
              throw new Error('画像URLが返されませんでした');
            }
          } catch (error) {
            console.error('画像アップロードエラー:', error);
            reject(error);
          }
        };
        
        reader.onerror = () => {
          console.error('ファイル読み込みエラー');
          reject(new Error('ファイルの読み込みに失敗しました'));
        };
        
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('画像アップロードエラー:', error);
      return null;
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setError(''); // エラーメッセージをクリア
      
      // ファイルをプレビュー表示
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // 画像をアップロード
      try {
        const uploadedUrl = await uploadImage(file, 'avatar');
        if (uploadedUrl) {
          setProfileData(prev => ({
            ...prev,
            avatarUrl: uploadedUrl
          }));
          console.log('アバター画像がアップロードされました:', uploadedUrl);
        }
      } catch (error) {
        console.error('アバター画像のアップロードに失敗しました:', error);
        setError('アバター画像のアップロードに失敗しました');
      }
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setError(''); // エラーメッセージをクリア
      
      // ファイルをプレビュー表示
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // 画像をアップロード
      try {
        const uploadedUrl = await uploadImage(file, 'cover');
        if (uploadedUrl) {
          setProfileData(prev => ({
            ...prev,
            coverUrl: uploadedUrl
          }));
          console.log('カバー画像がアップロードされました:', uploadedUrl);
        }
      } catch (error) {
        console.error('カバー画像のアップロードに失敗しました:', error);
        setError('カバー画像のアップロードに失敗しました');
      }
    }
  };

  // 電話番号の変更（旧番号は2年間ブロックされる）
  const handleChangePhone = async () => {
    setPhoneChangeMsg('');
    setPhoneChangeErr('');
    const digits = (newPhone || '').replace(/[^0-9]/g, '');
    if (digits.length < 10 || digits.length > 11) {
      setPhoneChangeErr('電話番号は10桁または11桁で入力してください');
      return;
    }
    if (!window.confirm('電話番号を変更しますか？\n変更前の番号は2年間、新規登録に使用できなくなります。')) {
      return;
    }
    try {
      setPhoneSubmitting(true);
      const res = await apiService.put('/api/auth/users/phone', { phone_number: newPhone });
      const data = res.data || {};
      if (data.error) {
        setPhoneChangeErr(data.error);
      } else {
        setPhoneChangeMsg(data.message || '電話番号を変更しました');
        setProfileData(prev => ({ ...prev, phoneNumber: data.phone_number || newPhone }));
        setNewPhone('');
      }
    } catch (err) {
      setPhoneChangeErr(err.response?.data?.error || err.response?.data?.detail || '電話番号の変更に失敗しました');
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // 生年月日のバリデーション
      if (profileData.birthDate && !validateBirthDate(profileData.birthDate)) {
        setError('生年月日は18歳以上である必要があります');
        return;
      }
      
      setPageLoading(true);
      
      // 開発環境用のモック処理（実際のAPIが利用できない場合）
      const useMockApi = false; // 本番環境では必ずfalseに設定
      
      if (!useMockApi) {
        // 本番環境: 実際のAPIを使用してプロフィール情報を更新
        
        // 更新するデータをPUT形式に合わせる
        const updatedData = {
          display_name: profileData.displayName,
          bio: profileData.bio,
          location: profileData.location,
          website: profileData.website,
          birth_date: profileData.birthDate,
          gender: profileData.gender,
          prefecture_code: profileData.prefecture_code // 明示的に都道府県コードを送信
        };

        // アバターとカバー画像は新しくアップロードされた場合のみ更新
        if (profileData.avatarUrl && !profileData.avatarUrl.startsWith('data:')) {
          updatedData.avatar = profileData.avatarUrl;
        }
        if (profileData.coverUrl && !profileData.coverUrl.startsWith('data:')) {
          updatedData.cover_image = profileData.coverUrl;
        }
        
        // 投稿ユーザーの場合は一つのAPIで全て更新、そうでなければ通常のプロフィール更新
        if (isPosterUser) {
          // バリデーション
          if (posterProfile.shop_name && !validateNumberField(posterProfile.age, 18, 100)) {
            setError('年齢は18歳以上で入力してください');
            setPageLoading(false);
            return;
          }
          
          if (posterProfile.height && !validateNumberField(posterProfile.height, 140, 200)) {
            setError('身長は140cm〜200cmの範囲で入力してください');
            setPageLoading(false);
            return;
          }
          
          if (posterProfile.bust && !validateNumberField(posterProfile.bust, 70, 150)) {
            setError('バストは70cm〜150cmの範囲で入力してください');
            setPageLoading(false);
            return;
          }
          
          if (posterProfile.waist && !validateNumberField(posterProfile.waist, 50, 120)) {
            setError('ウエストは50cm〜120cmの範囲で入力してください');
            setPageLoading(false);
            return;
          }
          
          if (posterProfile.hip && !validateNumberField(posterProfile.hip, 70, 150)) {
            setError('ヒップは70cm〜150cmの範囲で入力してください');
            setPageLoading(false);
            return;
          }
          
          // 投稿ユーザー情報の更新（基本プロフィール + 投稿ユーザー特有データを一度に更新）
          const combinedData = {
            // 基本プロフィール情報
            display_name: profileData.displayName,
            bio: profileData.bio,
            location: profileData.location,
            website: profileData.website,
            prefecture_code: profileData.prefecture_code,
            birth_date: profileData.birthDate,
            gender: profileData.gender,
            avatar: profileData.avatarUrl || (avatarPreview !== currentUser?.avatar ? avatarPreview : undefined),
            cover_image: profileData.coverUrl || (coverPreview !== currentUser?.coverImage ? coverPreview : undefined),
            
            // 投稿ユーザー特有データ
            ...posterProfile
          };
          
          const posterResponse = await apiService.put(`/api/users/poster_profile`, combinedData);
          
        } else {
          // 投稿ユーザー以外の場合は従来の方法でプロフィール更新
          const profileResponse = await apiService.put(`/api/profiles/update`, updatedData);
          
          // 都道府県コードを更新するための追加リクエスト
          if (profileData.prefecture_code) {
            try {
              await apiService.post(`/api/users/update_prefecture`, {
                prefecture_code: profileData.prefecture_code
              });
            } catch (prefErr) {
              
            }
          }
        }
        
        // ユーザー情報をローカルストレージにも更新
        const userData = {
          ...currentUser,
          displayName: profileData.displayName,
          bio: profileData.bio,
          location: profileData.location,
          website: profileData.website,
          avatar: profileData.avatarUrl || avatarPreview,
          coverImage: profileData.coverUrl || coverPreview,
          birthDate: profileData.birthDate,
          gender: profileData.gender,
          prefecture_code: profileData.prefecture_code
        };
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        // 開発環境: モック処理
        
        if (isPosterUser) {
          
        }
        
        // 処理時間をシミュレート
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // ローカルストレージにユーザーデータを保存（モック用）
        const userData = {
          ...currentUser,
          displayName: profileData.displayName,
          bio: profileData.bio,
          location: profileData.location,
          website: profileData.website,
          avatar: profileData.avatarUrl || avatarPreview,
          coverImage: profileData.coverUrl || coverPreview,
          birthDate: profileData.birthDate,
          gender: profileData.gender,
          prefecture_code: profileData.prefecture_code
        };
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      // 更新が成功したらプロフィールページにリダイレクト（強制リロード）
      window.location.href = `/profile/${profileData.username}`;
    } catch (err) {
      setError('プロフィールの更新に失敗しました。');
      setPageLoading(false);
      
      
      // エラーが発生した場合でもモック応答として成功とみなす
      
      setTimeout(() => {
        window.location.href = `/profile/${profileData.username}`;
      }, 1000);
    }
  };

  // 生年月日フィールドの最大値（18歳以上の制限）を計算
  const calculateMaxDate = () => {
    const today = new Date();
    const minAge = 18;
    today.setFullYear(today.getFullYear() - minAge);
    return today.toISOString().split('T')[0]; // YYYY-MM-DD形式
  };

  if (loading || (pageLoading && !profileData.displayName)) {
    return (
      <div className="profileEdit__loading">
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="profileEdit">
      <div className="profileEdit__header">
        <ArrowBackIcon className="profileEdit__backButton" onClick={() => window.history.back()} />
        <div>
          <h2>プロフィールを編集</h2>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate('/blocked-users')}
            title="ブロック一覧"
            color="default"
            size="medium"
          >
            <PersonOffIcon />
          </IconButton>
          <Button 
            variant="contained" 
            className="profileEdit__saveButton"
            onClick={handleSubmit}
          >
            保存
          </Button>
        </div>
      </div>

      <form className="profileEdit__form" onSubmit={handleSubmit}>
        {/* カバー画像セクション */}
        <div className="profileEdit__coverSection">
          <div 
            className="profileEdit__coverPreview" 
            style={{ 
              backgroundImage: (coverPreview || profileData.coverImage || profileData.coverUrl) 
                ? `url(${coverPreview || profileData.coverImage || profileData.coverUrl})` 
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: '200px',
              width: '100%',
              borderRadius: '8px',
              marginBottom: '15px',
              backgroundColor: '#e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#757575',
              fontSize: '14px'
            }}
            onClick={() => console.log('Cover image URLs:', {
              coverPreview,
              coverImage: profileData.coverImage,
              coverUrl: profileData.coverUrl
            })}
          >
            {!(coverPreview || profileData.coverImage || profileData.coverUrl) && (
              'カバー画像なし'
            )}
          </div>
          <input
            accept="image/*"
            id="cover-upload"
            type="file"
            style={{ display: 'none' }}
            onChange={handleCoverChange}
          />
          <label htmlFor="cover-upload">
            <Button variant="outlined" component="span" className="profileEdit__uploadButton">
              カバー画像を変更
            </Button>
          </label>
        </div>

        <div className="profileEdit__avatarSection">
          <Avatar 
            src={avatarPreview || profileData.avatar || profileData.avatarUrl || '/images/default-avatar.png'} 
            className="profileEdit__avatar"
            onClick={() => console.log('Avatar URLs:', {
              avatarPreview,
              avatar: profileData.avatar,
              avatarUrl: profileData.avatarUrl
            })}
            onError={(e) => console.error('Avatar failed to load:', e.target.src)}
          />
          <input
            accept="image/*"
            id="avatar-upload"
            type="file"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />
          <label htmlFor="avatar-upload">
            <Button variant="outlined" component="span" className="profileEdit__uploadButton">
              プロフィール画像を変更
            </Button>
          </label>
        </div>
        
        {error && <p className="profileEdit__error">{error}</p>}
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          基本情報
        </Typography>
        
        <TextField
          label="表示名"
          name="displayName"
          value={profileData.displayName}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          required
        />
        
        <TextField
          label="ユーザー名"
          name="username"
          value={profileData.username}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          required
          disabled // ユーザー名は変更不可とする
        />
        
        <TextField
          label="自己紹介"
          name="bio"
          value={profileData.bio}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          multiline
          rows={3}
          inputProps={{ maxLength: 160 }}
          helperText={`${profileData.bio.length}/160`}
        />
        
        <TextField
          label="場所"
          name="location"
          value={profileData.location}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
        />
        
        {/* Webサイト */}
        <TextField
          name="website"
          label="Webサイト"
          variant="outlined"
          fullWidth
          value={profileData.website || ''}
          onChange={handleInputChange}
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkIcon />
              </InputAdornment>
            ),
          }}
        />
        
        {/* 都道府県 */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="prefecture-label">都道府県</InputLabel>
          <Select
            labelId="prefecture-label"
            name="prefecture_code"
            value={profileData.prefecture_code || ''}
            onChange={handleInputChange}
            label="都道府県"
          >
            <MenuItem value="">
              <em>選択なし</em>
            </MenuItem>
            {prefectures.map((pref) => (
              <MenuItem key={pref.code} value={pref.code}>
                {pref.name}
              </MenuItem>
            ))}
          </Select>
          {profileData.prefecture_code && (
            <FormHelperText>選択中: {prefectures.find(p => p.code === profileData.prefecture_code)?.name || profileData.prefecture_code}</FormHelperText>
          )}
        </FormControl>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          個人情報
        </Typography>
        
        {/* 生年月日 */}
        <TextField
          label="生年月日"
          name="birthDate"
          type="date"
          value={profileData.birthDate}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            max: calculateMaxDate() // 18歳未満の日付は選択不可
          }}
          helperText={profileData.birthDate ? "生年月日は一度設定すると変更できません" : "18歳以上である必要があります"}
          disabled={!!profileData.birthDate} // 既に値がある場合は編集不可
        />
        
        {/* 性別 */}
        <FormControl component="fieldset" fullWidth margin="normal">
          <FormLabel component="legend">性別</FormLabel>
          <RadioGroup
            row
            name="gender"
            value={profileData.gender}
            onChange={handleInputChange}
            disabled={!!profileData.gender} // 既に値がある場合は編集不可
          >
            <FormControlLabel value="male" control={<Radio />} label="男性" disabled={!!profileData.gender} />
            <FormControlLabel value="female" control={<Radio />} label="女性" disabled={!!profileData.gender} />
            <FormControlLabel value="other" control={<Radio />} label="その他" disabled={!!profileData.gender} />
          </RadioGroup>
          <FormHelperText>性別は一度設定すると変更できません</FormHelperText>
        </FormControl>

        {/* 電話番号の変更 */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          電話番号
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          現在の電話番号: {profileData.phoneNumber || '未登録'}
        </Typography>
        {phoneChangeMsg && <Alert severity="success" sx={{ mb: 1 }}>{phoneChangeMsg}</Alert>}
        {phoneChangeErr && <Alert severity="error" sx={{ mb: 1 }}>{phoneChangeErr}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="新しい電話番号"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            placeholder="09012345678"
            helperText="変更前の番号は2年間、新規登録に使用できなくなります"
            inputProps={{ maxLength: 11 }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <Button
            variant="outlined"
            onClick={handleChangePhone}
            disabled={phoneSubmitting || !newPhone}
            sx={{ mt: 1 }}
          >
            {phoneSubmitting ? '変更中...' : '電話番号を変更'}
          </Button>
        </Box>
        
        {/* プライバシー・セキュリティ */}
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          プライバシー・セキュリティ
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<PersonOffIcon />}
            onClick={() => navigate('/blocked-users')}
            fullWidth
            sx={{ 
              justifyContent: 'flex-start',
              textAlign: 'left',
              p: 2
            }}
          >
            ブロック済みアカウント
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            ブロックしたユーザーの管理・解除ができます
          </Typography>
        </Box>
        
        {/* 投稿ユーザー情報 */}
        {isPosterUser && (
          <>
            <Divider sx={{ my: 4 }} />
            
            <Typography variant="h6" gutterBottom>
              店舗・身体情報
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="店名"
                  name="shop_name"
                  value={posterProfile.shop_name}
                  onChange={handlePosterInputChange}
                  helperText="投稿時に固定タグとして設定されます"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>都道府県</InputLabel>
                  <Select
                    name="prefecture_code"
                    value={posterProfile.prefecture_code}
                    onChange={handlePosterInputChange}
                    label="都道府県"
                  >
                    <MenuItem value="">選択なし</MenuItem>
                    {prefectures.map(prefecture => (
                      <MenuItem key={prefecture.code} value={prefecture.code}>
                        {prefecture.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>都道府県を選択してください</FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!posterProfile.prefecture_code}>
                  <InputLabel>市区町村</InputLabel>
                  <Select
                    name="city_code"
                    value={posterProfile.city_code}
                    onChange={handlePosterInputChange}
                    label="市区町村"
                  >
                    <MenuItem value="">選択なし</MenuItem>
                    {cities.map(city => (
                      <MenuItem key={city.code} value={city.code}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>市区町村を選択してください</FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="源氏名"
                  name="stage_name"
                  value={posterProfile.stage_name}
                  onChange={handlePosterInputChange}
                  helperText="源氏名を入力してください"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="年齢"
                  name="age"
                  type="number"
                  value={posterProfile.age}
                  onChange={handlePosterInputChange}
                  InputProps={{ inputProps: { min: 18, max: 100 } }}
                  helperText="18歳以上で入力してください"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="身長 (cm)"
                  name="height"
                  type="number"
                  value={posterProfile.height}
                  onChange={handlePosterInputChange}
                  InputProps={{ inputProps: { min: 140, max: 200 } }}
                  helperText="140cm〜200cmで入力してください"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="バスト (cm)"
                  name="bust"
                  type="number"
                  value={posterProfile.bust}
                  onChange={handlePosterInputChange}
                  InputProps={{ inputProps: { min: 70, max: 150 } }}
                  helperText="70cm〜150cmで入力してください"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>カップ数</InputLabel>
                  <Select
                    name="cup_size"
                    value={posterProfile.cup_size}
                    onChange={handlePosterInputChange}
                    label="カップ数"
                  >
                    <MenuItem value="">選択なし</MenuItem>
                    {CUP_SIZES.map(size => (
                      <MenuItem key={size} value={size}>
                        {size}カップ
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>カップ数を選択してください</FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ウエスト (cm)"
                  name="waist"
                  type="number"
                  value={posterProfile.waist}
                  onChange={handlePosterInputChange}
                  InputProps={{ inputProps: { min: 50, max: 120 } }}
                  helperText="50cm〜120cmで入力してください"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ヒップ (cm)"
                  name="hip"
                  type="number"
                  value={posterProfile.hip}
                  onChange={handlePosterInputChange}
                  InputProps={{ inputProps: { min: 70, max: 150 } }}
                  helperText="70cm〜150cmで入力してください"
                />
              </Grid>
            </Grid>
          </>
        )}
      </form>
    </div>
  );
}

export default ProfileEdit; 